import { useState } from 'react';
import type { JSX } from 'react';
import { useI18n } from '../../i18n';
import { LanguagePickerSheet } from './LanguagePickerSheet';

/**
 * The language picker, opened and closed.
 *
 * Two screens own a picker of their own and neither can borrow the other's:
 * `MainShell` holds the one behind the three tabs, and `KeyEntryScreen` lives
 * *outside* the shell — it is the one place a device can be handed over already
 * showing a language the operator cannot read, and it is the only screen the tab
 * bar cannot reach. So there are two instances on purpose; what there is no
 * reason to have twice is the three-line dance around them, which was written
 * out verbatim in both: the same `open` flag, the same `onPick` that sets the
 * locale and then closes, the same `onDismiss`.
 *
 * **Closing on pick is the part worth keeping in one place.** A picker that sets
 * the language and stays up looks like it did nothing — the sheet covers the
 * screen whose words just changed — and that is a bug each copy could grow on
 * its own.
 *
 * It lives in `components/ui/` rather than `src/hooks/` because it is the sheet:
 * it holds no app state, decides nothing, and hands back the element it renders.
 * `src/hooks/` is for state the app runs on (`useCheckLoop` and its scheduler),
 * and a picker's open flag is not that.
 */
export interface LanguageSheet {
  /** Show the sheet. Wire straight to whatever the screen's language button is. */
  open(): void;
  /**
   * The sheet itself, or `null` while it is closed. Render it last in the
   * screen — it portals to `document.body`, so its position in the tree decides
   * nothing about where it draws, but keeping it at the end is what makes the
   * "there is a modal here" line easy to find.
   */
  sheet: JSX.Element | null;
}

export function useLanguageSheet(): LanguageSheet {
  const { locale, setLocale } = useI18n();
  const [isOpen, setIsOpen] = useState(false);

  return {
    open: () => setIsOpen(true),
    sheet: isOpen ? (
      <LanguagePickerSheet
        current={locale}
        onPick={(tag) => {
          setLocale(tag);
          setIsOpen(false);
        }}
        onDismiss={() => setIsOpen(false)}
      />
    ) : null,
  };
}
